/**
 * RÔLES D'OMBRE DU WEB (charte §11) — trois profondeurs nommées, et rien d'autre.
 *
 * ⚠️ POURQUOI UN FICHIER SÉPARÉ DE `table.ts` : ces trois rôles servent au picker (§1), au fantôme de
 * drag (§4), à la modale (§6) autant qu'au popover Colonnes du tableau. Les loger dans `table.ts`
 * obligerait le picker à importer le tableau — un couplage qui n'a aucune raison d'exister.
 *
 * ⚠️ POURQUOI DES RÔLES ET PAS DES OMBRES AD HOC : sans jeu nommé, chaque composant réinvente sa
 * profondeur et l'interface perd sa hiérarchie de plans. Le §11 supprime exactement ça.
 */

/** Un rôle d'ombre, en géométrie pure : c'est ce qui permet de le rendre aussi bien en CSS qu'ailleurs. */
export interface ShadowSpec {
  offsetY: number;
  blur: number;
  spread: number;
  /** Part de l'encre profonde du thème, entre 0 et 1. */
  alpha: number;
}

export const SHADOW_WEB = {
  /** Pickers, popover Colonnes, menus ancrés. */
  popover: { offsetY: 18, blur: 44, spread: -12, alpha: 0.28 },
  /** Fantôme de drag (réordonnancement de colonnes, tuiles, photos). */
  lifted: { offsetY: 18, blur: 40, spread: -12, alpha: 0.45 },
  /** Modales et dialogues. */
  modal: { offsetY: 28, blur: 70, spread: -10, alpha: 0.38 },
} as const satisfies Record<string, ShadowSpec>;

export type ShadowRole = keyof typeof SHADOW_WEB;

/**
 * RELIEF MINIMAL — la RÉPARATION d'un jeton fantôme, pas un quatrième rôle.
 *
 * ⚠️ `.seg-track > .seg.active` référence `var(--sh-sm, …)` depuis `theme-web.ts` : tant qu'aucun thème
 * ne l'émet, c'est le repli littéral qui gagne — donc une ombre qui ne suit ni le thème ni l'accent de
 * l'app. On répare cette classe de défaut ici, avant de la reproduire avec trois jetons de plus.
 * ⚠️ ET UNE APP QUI LE DÉCLARE CHEZ ELLE ANNULE CETTE RÉPARATION SANS ERREUR : une déclaration locale
 * bat le jeton émis, donc l'app retombe sur une ombre figée pendant que la charte croit la fournir.
 * L'EDI a été dans ce cas (`rgba()` en dur dans sa feuille), et rien ne le signalait — seule la lecture.
 * ⚠️ Il n'entre PAS dans `SHADOW_WEB` : la charte §11 en compte trois, et un « rôle » de plus dilué
 * dans le jeu ferait croire à une quatrième profondeur de plan. C'est un relief de surface, pas un plan.
 *
 * ⚠️ RECALIBRÉ SUR LA MAQUETTE (2026-08-16) : `0 1px 3px …/.16` y est écrit 14 fois, contre 2 fois
 * pour le `1px 2px …/.06` que ce jeton portait. À .08, la pastille active du segmenté ne se
 * DÉCOLLAIT PAS de sa piste — or c'est le seul signe qui distingue l'option en vigueur des autres.
 */
export const SHADOW_WEB_SM: ShadowSpec = { offsetY: 1, blur: 3, spread: 0, alpha: 0.16 };

/**
 * Jeton d'encre dont l'ombre se dérive, PAR MODE.
 *
 * ⚠️ LE PIÈGE, ET LA RAISON DE CES DEUX CONSTANTES : la charte §11 dit « la couleur se dérive du rôle
 * `ink-950` du thème courant ». Or l'échelle d'encre de ce dépôt est INVERSÉE en sombre — `inkDark[950]`
 * vaut `#FFFFFF`. Appliquée telle quelle, la règle produirait un HALO BLANC sous chaque popover en
 * thème sombre. En sombre on dérive donc l'ombre de `--canvas`, qui est la surface la plus profonde du
 * mode (et vaut exactement le `#0c0e10` de `ink-950` clair) : l'intention du §11 est respectée, sa
 * lettre ne l'aurait pas été. Divergence à consigner, pas à corriger dans la source de dessin.
 */
export const SHADOW_INK_LIGHT = '--ink-950';
export const SHADOW_INK_DARK = '--canvas';

/**
 * Rend un rôle en `box-shadow` CSS.
 *
 * ⚠️ `color-mix` plutôt qu'un `rgba()` calculé : la couleur reste une VARIABLE du thème, donc une app
 * qui surcharge ses gris voit son ombre suivre. Un rgba figé ne suivrait rien — c'est le noir codé en
 * dur que le §11 interdit, déguisé.
 */
export function shadowCss(role: ShadowRole, inkVar: string = SHADOW_INK_LIGHT): string {
  return specToCss(SHADOW_WEB[role], inkVar);
}

/** Même rendu pour le relief minimal (cf. `SHADOW_WEB_SM`). */
export function shadowSmCss(inkVar: string = SHADOW_INK_LIGHT): string {
  return specToCss(SHADOW_WEB_SM, inkVar);
}

/**
 * RELIEF DU BOUTON D'ACTION — le seul bouton qui se décolle de sa surface.
 *
 * ⚠️ IL N'EST PAS UN QUATRIÈME PLAN, comme `SHADOW_WEB_SM` n'en était pas un : la charte §11 compte
 * TROIS profondeurs (popover, lifted, modal) et les diluer ferait perdre la hiérarchie qu'elles
 * disent. C'est un relief de surface, plus ample que celui du segmenté parce que le bouton est
 * lui-même plus haut.
 *
 * ⚠️ SA COULEUR VIENT DE L'ENCRE DU THÈME, PLUS DE LA MARQUE (retour utilisateur 2026-09-07 : « on a
 * une sorte de border en vert sombre qui ne rend pas très bien »). Elle était tirée du teal à 95 % :
 * sur fond clair ça passait pour un liseré terne, sur fond SOMBRE ça devenait un halo vert franc
 * autour du bouton. Une ombre est un manque de lumière, pas une teinte de marque — et dérivée de
 * l'encre, elle suit désormais le mode comme les trois autres.
 */
export const SHADOW_WEB_CTA: ShadowSpec = { offsetY: 8, blur: 18, spread: -10, alpha: 0.28 };

/**
 * LE MÊME RELIEF, AU SURVOL — le bouton monte, il ne s'allume pas.
 * ⚠️ Il existe parce que le survol portait sa PROPRE ombre teal, écrite en dur dans la feuille : la
 * corriger au repos et la laisser au survol aurait mis deux règles contradictoires à trois lignes
 * d'écart. L'écart se voit d'autant moins qu'il ne se manifeste que sous le curseur.
 */
export const SHADOW_WEB_CTA_HOVER: ShadowSpec = { offsetY: 12, blur: 26, spread: -12, alpha: 0.34 };

/** Même rendu pour le relief du bouton d'action (cf. `SHADOW_WEB_CTA`). */
export function shadowCtaCss(inkVar: string = SHADOW_INK_LIGHT, survol = false): string {
  return specToCss(survol ? SHADOW_WEB_CTA_HOVER : SHADOW_WEB_CTA, inkVar);
}

function specToCss(s: ShadowSpec, inkVar: string): string {
  const pct = Math.round(s.alpha * 100);
  return `0 ${s.offsetY}px ${s.blur}px ${s.spread}px color-mix(in srgb, var(${inkVar}) ${pct}%, transparent)`;
}

/**
 * Variables à émettre par `webThemeVars()` : `--sh-popover`, `--sh-lifted`, `--sh-modal`, `--sh-sm`.
 *
 * ⚠️ Émises en VARIABLES et pas seulement disponibles en TS : une feuille d'app ne peut pas importer un
 * token TypeScript. Un jeton qui ne descend pas jusqu'au CSS n'est pas un jeton partagé — c'est la leçon
 * déjà payée par `--h-*` (30/36/40 recopiés à la main dans chaque feuille).
 */
export function shadowVars(inkVar: string = SHADOW_INK_LIGHT): Record<string, string> {
  const vars: Record<string, string> = { '--sh-sm': shadowSmCss(inkVar) };
  for (const role of Object.keys(SHADOW_WEB) as ShadowRole[]) vars[`--sh-${role}`] = shadowCss(role, inkVar);
  return vars;
}
